import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Icone } from './ui';

// Coquille de l'espace Pro, sur le modèle d'Opal Dental Lab : une colonne de
// navigation fixe à gauche, un fond clair, et le contenu qui respire à droite.
// Sept onglets tiennent sans effort dans une colonne verticale, là où une
// barre horizontale les comprimait puis les coupait dès l'iPad.

const NAV = [
  { to: '/pro', label: 'Pipeline', icone: 'pipeline' },
  { to: '/pro/deviseur', label: 'Nouveau devis', icone: 'plus' },
  { to: '/pro/chantiers', label: 'Chantiers', icone: 'chantier' },
  { to: '/pro/referentiel', label: 'Prix', icone: 'euro' },
  { to: '/pro/equipe', label: 'Équipe', icone: 'equipe' },
  { to: '/pro/comptes', label: 'Comptes', icone: 'cle', patron: true },
];

export default function ProLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const token = localStorage.getItem('pro_token');
  const utilisateur = localStorage.getItem('pro_user');
  const role = localStorage.getItem('pro_role') || 'conducteur';
  const [ouvert, setOuvert] = useState(false);

  useEffect(() => {
    if (!token) navigate('/pro/login', { replace: true });
  }, [token, navigate]);

  // Le menu mobile se referme en changeant d'écran : sinon il reste ouvert
  // par-dessus la page qu'on vient de demander.
  useEffect(() => { setOuvert(false); }, [pathname]);

  const deconnexion = () => {
    localStorage.removeItem('pro_token');
    localStorage.removeItem('pro_user');
    localStorage.removeItem('pro_role');
    navigate('/pro/login');
  };

  if (!token) return null;

  const liens = NAV.filter(n => !n.patron || role === 'patron');

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <button
        onClick={() => setOuvert(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md border border-slate-200"
        aria-label="Ouvrir le menu">
        <svg className="w-5 h-5 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
        </svg>
      </button>

      {ouvert && <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setOuvert(false)} />}

      <aside className={`fixed top-0 left-0 h-full w-64 max-w-[85vw] bg-white border-r border-slate-200 z-50
                         transform transition-transform lg:translate-x-0 ${ouvert ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Colonne : en-tête fixe, navigation défilante, pied fixe — la liste
            ne doit pas pousser la déconnexion hors de l'écran. */}
        <div className="h-full flex flex-col">
          <div className="p-6 pb-0 shrink-0">
            <div className="flex items-center justify-between mb-7">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center font-black text-white text-sm">A</div>
                <div className="leading-none">
                  <span className="font-black tracking-wider text-[15px] text-slate-900">APEX</span>
                  <span className="font-light text-[15px] text-orange-600 ml-1">Pro</span>
                </div>
              </div>
              <button onClick={() => setOuvert(false)} className="lg:hidden p-1 text-slate-400" aria-label="Fermer le menu">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="mb-5 pb-5 border-b border-slate-100">
              <p className="font-semibold text-slate-900 text-sm">{utilisateur}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {role === 'patron' ? 'Direction' : 'Conduite de travaux'}
              </p>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto px-4 pb-4 space-y-0.5">
            {liens.map(({ to, label, icone }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/pro'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? 'bg-orange-50 text-orange-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`
                }
              >
                <Icone nom={icone} className="w-[18px] h-[18px] shrink-0" />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="shrink-0 p-6 border-t border-slate-100">
            <button onClick={deconnexion}
                    className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700 transition-colors">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M15 17l5-5-5-5M20 12H9M12 20H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {/* min-w-0 : sans lui, un tableau ou un nom sans espace impose sa largeur
          au lieu de se réduire, et la page déborde. */}
      <main className="flex-1 min-w-0 lg:ml-64 p-5 pt-16 lg:p-8 lg:pt-8">
        <Outlet />
      </main>
    </div>
  );
}
